import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PROFILE_STATUS } from 'services/auth/newFlow/constants';
import { userSelector } from 'selectors/user/userSelector';
import cmsDataSelector from 'selectors/page/autoReplenishment/cmsData/cmsDataSelector';
import BeautyInsiderSelector from 'selectors/beautyInsider/beautyInsiderSelector';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { getFeatureFlags } from 'components/RichProfile/MyAccount/AutoReplenishment/utils/featureFlags';
import MANAGE_SUBS_EN from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_en_US.js';
import MANAGE_SUBS_FR from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_fr_CA.js';
import AUTO_REPLENISHMENT_EN from 'components/RichProfile/MyAccount/AutoReplenishment/locales/AutoReplenishment_en_US.js';
import AUTO_REPLENISHMENT_FR from 'components/RichProfile/MyAccount/AutoReplenishment/locales/AutoReplenishment_fr_CA.js';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const { beautyInsiderSelector } = BeautyInsiderSelector;

const getText = getLocalisedDataFromResource(AUTO_REPLENISHMENT_EN, AUTO_REPLENISHMENT_FR);
const getTextManageSubs = getLocalisedDataFromResource(MANAGE_SUBS_EN, MANAGE_SUBS_FR);

/**
 * Provides state and memoized localization strings for the AutoReplenishment hub.
 * Selects auth, user, CMS, and Beauty Insider data from the Redux store, and
 * builds localization from AutoReplenishment and ManageSubscriptionModal locale files.
 */
export function useAutoReplenishmentHub() {
    const biPoints = useSelector(state => beautyInsiderSelector(state).summary?.clientSummary?.currentYear?.subscriptionPtsEarned?.value || 0);
    const cmsData = useSelector(cmsDataSelector);
    const isLoggedIn = useSelector(state => state.auth?.profileStatus === PROFILE_STATUS.LOGGED_IN);
    const user = useSelector(userSelector);

    const displaySignIn = !isLoggedIn && user.isInitialized;

    const featureFlags = useMemo(getFeatureFlags, []);

    const localization = useMemo(
        () => ({
            activeSubscriptionsCount: getText('activeSubscriptionsCount', ['{0}', '{1}']),
            activeText: getTextManageSubs('active'),
            autoReplenishSummary: getTextManageSubs('autoReplenishSummary'),
            biPointsEarned: getTextManageSubs('biPointsEarned'),
            canceledSubscriptionsCount: getText('canceledSubscriptionsCount', ['{0}', '{1}']),
            canceledSubscriptionsHeading: getText('canceledSubscriptionsHeading'),
            canceledSubscriptionsSubheading: getText('canceledSubscriptionsSubheading'),
            canceledText: getText('canceled'),
            cvvSubmitButtonText: getTextManageSubs('cvvSubmitButtonText'),
            deliveryFrequency: getTextManageSubs('deliveryFrequency'),
            headerItem: getText('headerItem'),
            headerPrice: getText('headerPrice'),
            needHelp: getText('needHelp'),
            pausedSubscriptionsCount: getText('pausedSubscriptionsCount', ['{0}', '{1}']),
            pausedText: getTextManageSubs('paused'),
            points: getTextManageSubs('points'),
            rateOf: getText('rateOf', ['{0}']),
            totalSaving: getTextManageSubs('totalSaving'),
            viewFAQ: getText('viewFAQ'),
            viewMore: getText('viewMore'),
            viewMoreSubscriptions: getText('viewMoreSubscriptions'),
            viewShipments: getTextManageSubs('viewShipments')
        }),
        []
    );

    return {
        localization,
        featureFlags,
        displaySignIn,
        cmsData,
        biPoints,
        user,
        isLoggedIn
    };
}
